import type { Session } from '@supabase/supabase-js';
import { z } from 'zod';

import { apiFetch } from '@/lib/api';

// Client contract for GET /api/v1/admin/ai-usage?days=N. This schema is the
// source of truth the API endpoint conforms to; Zod-parsed on arrival so any
// drift surfaces loudly instead of mis-rendering the panel.
//
// Costs are integer MICRO-USD (millionths of a dollar) — the panel divides by
// 1e6 to display dollars. Cost is an estimate (see apps/api/src/ai/pricing.ts).

const UsageAggregateSchema = z.object({
  requests: z.number().int().nonnegative(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  estimatedCostMicros: z.number().int().nonnegative(),
});

export const AiUsageSummarySchema = z.object({
  window: z.object({
    days: z.number().int().positive(),
    // ISO 8601 — start of the reporting window.
    since: z.string(),
  }),
  totals: UsageAggregateSchema,
  byProvider: z.array(
    UsageAggregateSchema.extend({
      provider: z.enum(['gemini', 'openai', 'claude']),
      model: z.string(),
    }),
  ),
  // Ranked by estimated cost, highest first. At most 10 entries.
  topOperators: z.array(
    UsageAggregateSchema.extend({
      operatorId: z.string(),
      operatorName: z.string(),
      operatorSlug: z.string(),
    }),
  ),
});

export type AiUsageSummary = z.infer<typeof AiUsageSummarySchema>;
export type AiUsageProviderRow = AiUsageSummary['byProvider'][number];
export type AiUsageOperatorRow = AiUsageSummary['topOperators'][number];

export const fetchAiUsage = (
  session: Session,
  days: number,
  signal?: AbortSignal,
): Promise<AiUsageSummary> =>
  apiFetch(`/api/v1/admin/ai-usage?days=${String(days)}`, {
    session,
    schema: AiUsageSummarySchema,
    ...(signal ? { signal } : {}),
  });
